import { defineService } from '@nzyme/ioc/Service.js';
import { Dexie } from 'dexie';
import { AppConfig } from '@sigma/common/AppConfig.js';
import { AuthStore } from '../auth/AuthStore.js';
class ChatPendingMessageDb extends Dexie {
    pendingMessages;
    constructor(dbName) {
        super(dbName);
        this.version(1).stores({
            pendingMessages: 'chatId, updatedAt',
        });
    }
}
/**
 * Shared client service persisting per-chat drafts and failed sends to IndexedDB.
 * Used by the doctor-start-chat flow (writes the outgoing message as "failed"
 * before navigation, then the landed chat view retries / clears it).
 */
export const ChatPendingMessageStore = defineService({
    name: 'ChatPendingMessageStore',
    deps: {
        appConfig: AppConfig,
        authStore: AuthStore,
    },
    setup({ appConfig, authStore }) {
        const db = new ChatPendingMessageDb(`${appConfig.name}:chat-pending-messages`);
        // Persisted drafts/failed messages belong to the currently logged-in user.
        // Wipe the entire table whenever the user changes (logout or account switch)
        // so stale entries from a previous session can't leak into the new one.
        authStore.events.userChanged.on(() => void db.pendingMessages.clear());
        return {
            getDraft,
            setDraft,
            clearDraft,
            getFailedMessage,
            setFailedMessage,
            clearFailedMessage,
            clearAll,
        };
        async function getDraft(chatId) {
            const record = await db.pendingMessages.get(String(chatId));
            return record?.draft ?? null;
        }
        async function setDraft(chatId, text) {
            await upsert(chatId, { draft: text });
        }
        async function clearDraft(chatId) {
            await upsert(chatId, { draft: null });
        }
        async function getFailedMessage(chatId) {
            const record = await db.pendingMessages.get(String(chatId));
            if (!record?.failed) {
                return null;
            }
            return {
                message: record.failed,
                files: record.files ?? undefined,
            };
        }
        async function setFailedMessage(chatId, text, files) {
            await upsert(chatId, { failed: text, files: files ?? null });
        }
        async function clearFailedMessage(chatId) {
            await upsert(chatId, { failed: null, files: null });
        }
        async function clearAll(chatId) {
            await upsert(chatId, { draft: null, failed: null, files: null });
        }
        async function upsert(chatId, patch) {
            const key = String(chatId);
            await db.transaction('rw', db.pendingMessages, async () => {
                const existing = await db.pendingMessages.get(key);
                const next = {
                    chatId: key,
                    draft: patch.draft !== undefined ? patch.draft : (existing?.draft ?? null),
                    failed: patch.failed !== undefined ? patch.failed : (existing?.failed ?? null),
                    files: patch.files !== undefined ? patch.files : (existing?.files ?? null),
                    updatedAt: Date.now(),
                };
                // Drop the record entirely when all fields are cleared so the DB
                // doesn't accumulate empty rows for every chat the user ever opened.
                if (next.draft === null && next.failed === null && next.files === null) {
                    await db.pendingMessages.delete(key);
                    return;
                }
                await db.pendingMessages.put(next);
            });
        }
    },
});
//# sourceMappingURL=ChatPendingMessageStore.js.map